import {useMemo, useState} from 'react';
import {Breadcrumb, ProgressIndicator} from '@/components/atoms';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {ActivitySetUp} from '@/containers';
import {useAppSelector} from '@/store';
import {
  resetActivityData,
  setActivityFormData
} from '@/store/slices/activitySlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  ACTIVITY_TYPE,
  isVideoActivityType,
  isFreeKickActivityType,
  isWheelOfFortuneActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';
import {COUNTRY_CODES_FREQUENCY} from '@/utils/constants/globalConstants';
import {DATE_VALIDATION_TYPE} from '@/utils/constants/commonConstants';
import {isInProgress, isPublished, getCurrencySymbol} from '@/utils/commons';
import {isActivitySaveDraftEnabled} from '@/helpers/activityUtils';
import {useDatePickerInput} from '@/components/atoms/DatePickerInput/datePickerInput-hook';
import {
  validateFormData,
  isInvalidPointExpirationData
} from '@/utils/activityValidation';
import {getFormDataWithOrder} from '@/utils/activityFormBuilders';
import {
  getProgressConfig,
  getProgressRoute,
  getNextRoute
} from '@/utils/activityProgressConfig';
import {getCustomModal} from '@/utils/activityModalHelper';
const AddActivity = () => {
  const [isCancel, setIsCancel] = useState(false);
  const {formData} = useSelector((state) => state.activity);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const isCountryCodeFrequency = useMemo(
    () => COUNTRY_CODES_FREQUENCY.includes(countryCode),
    [countryCode]
  );
  const localCurrencyCode = getCurrencySymbol(countryCode);
  const {
    handleDraft: handleReviewDraft,
    handleValidationModalClose,
    isNameValid,
    getActivityName,
    validateName,
    handleActivityDurationValidation,
    resetValidation,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity
  } = useActivityReview();

  const {activityType, status, startDate, endDate} = formData;

  const shouldValidateActivityStartDate = useMemo(() => {
    const isSurvey = activityType === ACTIVITY_TYPE.SURVEY;
    const isVideo = isVideoActivityType(activityType);
    if (isSurvey || isVideo) {
      return !isInProgress(status);
    }
    return !isPublished(status) && !isInProgress(status);
  }, [activityType, status]);
  const {validateDates: validateStartDate} = useDatePickerInput('startDate', [
    {type: 'required', msg: ` is required`},
    {
      type: DATE_VALIDATION_TYPE.START_DATE,
      msg: ` can't be in the past.`,
      endDate: endDate
    },
    {
      type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
      msg: `  Target can only start from a date in future`
    }
  ]);
  const {validateDates: validateEndDate} = useDatePickerInput('endDate', [
    {type: 'required', msg: ` is required`},
    {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` can't be in the past.`},
    {
      type: DATE_VALIDATION_TYPE.END_DATE,
      msg: ` can't be less than Start date.`,
      startDate: startDate
    }
  ]);

  const surveyEndDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.END_DATE,
        msg: 'End Date cannot be earlier than Start Date.',
        startDate,
        excludeLabel: true
      }
    ],
    [startDate]
  );
  const {validateDates: validateSurveyEndDate} = useDatePickerInput(
    'Active End Date *',
    surveyEndDateRules
  );

  const surveyStartDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.START_DATE,
        msg: ` can't be in the past.`,
        endDate
      },
      {
        type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
        msg: `  Target can only start from a date in future`
      }
    ],
    [endDate]
  );
  const {validateDates: validateSurveyStartDate} = useDatePickerInput(
    'Active Start Date *',
    surveyStartDateRules
  );
  const videoEndDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.END_DATE,
        msg: 'End Date cannot be earlier than Start Date.',
        startDate,
        excludeLabel: true
      }
    ],
    [startDate]
  );
  const {validateDates: validateVideoEndDate} = useDatePickerInput(
    'Video Active End Date *',
    videoEndDateRules
  );

  const videoStartDateRules = useMemo(
    () => [
      {
        type: DATE_VALIDATION_TYPE.START_DATE,
        msg: ` can't be in the past.`,
        endDate
      },
      {
        type: DATE_VALIDATION_TYPE.START_DATE_TODAY,
        msg: `  Target can only start from a date in future`
      }
    ],
    [endDate]
  );
  const {validateDates: validateVideoStartDate} = useDatePickerInput(
    'Video Active Start Date *',
    videoStartDateRules
  );

  const dateFns = {
    validateStartDate,
    validateEndDate,
    validateSurveyStartDate,
    validateSurveyEndDate,
    validateVideoStartDate,
    validateVideoEndDate
  };

  const isVideoActivity = isVideoActivityType(activityType);
  const isFreeKickGameActivity = isFreeKickActivityType(activityType);
  const isWheelOfFortuneActivity = isWheelOfFortuneActivityType(activityType);
  const isBackgammonGameActivity = isBackgammonActivityType(activityType);

  const {steps: progressSteps, titles: progressTitles} =
    getProgressConfig(activityType);

  const handleProgressStepClick = (step) => {
    const route = getProgressRoute(activityType, step);
    if (route) navigate(route);
  };

  const handleDraft = (event) => {
    event.stopPropagation();
    handleReviewDraft();
  };

  const handleNext = async () => {
    const uniqueActivityName = getActivityName();
    if (
      formData?.status == null &&
      activityType !== ACTIVITY_TYPE.CURRENCY_TO_POINTS
    ) {
      const isValidName = await validateName(uniqueActivityName);
      if (!isValidName) return;
    }

    if (
      activityType === ACTIVITY_TYPE.POINT_EXPIRATION &&
      isInvalidPointExpirationData({formData})
    ) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Please enter a valid frequency between 1 and 365 days',
          type: 'error'
        })
      );
      return;
    }

    const formDataWithOrder = getFormDataWithOrder({
      formData,
      activityType,
      isCountryCodeFrequency
    });
    dispatch(setActivityFormData(formDataWithOrder));

    if (
      activityType !== ACTIVITY_TYPE.POINT_EXPIRATION &&
      activityType !== ACTIVITY_TYPE.CURRENCY_TO_POINTS
    ) {
      let validationParams = {...formData, activityName: uniqueActivityName};
      const isDurationValid =
        await handleActivityDurationValidation(validationParams);
      if (!isDurationValid) {
        return;
      }
    }

    resetValidation();
    navigate(
      getNextRoute(
        activityType,
        isVideoActivity,
        isFreeKickGameActivity,
        isWheelOfFortuneActivity,
        isBackgammonGameActivity
      )
    );
  };

  const handleCancel = () => {
    setIsCancel(!isCancel);
  };

  const onClickPrimaryButton = () => {
    handleResetFormData();
    navigate(ROUTES.ACTIVITIES);
  };

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel);
  };

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false
    };
    dispatch(resetActivityData(defaultFormData));
  };

  const handleHideSaveDraft = () => {
    return formData.activityType === '' || formData.activityType === undefined;
  };

  const modalDeps = {
    isCancel,
    isNameValid,
    handleValidationModalClose,
    onClickPrimaryButton,
    onClickSecondaryButton,
    handleCancel
  };

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              label: '',
              href: ROUTES.HOME
            },
            {
              label: 'Loyalty Campaigns',
              href: ROUTES.ACTIVITIES
            },
            {
              label: 'Add Activity',
              href: ROUTES.ACTIVITIES
            }
          ]
        }}
        id={'aa_bradcrumb'}
      />

      <PageHeader title={'Add Activity'} />

      <ProgressIndicator
        currentStep={1}
        completed={false}
        steps={progressSteps}
        titles={progressTitles}
        onStepClick={handleProgressStepClick}
      />
      <br />
      <ActivitySetUp
        localCurrencyCode={localCurrencyCode}
        showToast={showToast}
        toastMessage={toastMessage}
        handleToastClose={handleToastClose}
        handleViewActivity={handleViewActivity}
      />
      {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL', modalDeps)}
      {isCancel && getCustomModal('CANCEL_MODAL', modalDeps)}
      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={validateFormData({
          formData,
          dateFns,
          isCountryCodeFrequency,
          validateStartDate: shouldValidateActivityStartDate
        })}
        hideSaveDraft={handleHideSaveDraft()}
        disableDraft={!isActivitySaveDraftEnabled(status)}
      />
    </>
  );
};
export default AddActivity;
